import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { Button, Card, Field, inputClass } from './ui.js';

type Provider = 'openai' | 'anthropic';
interface KeyStatus {
  keySet: boolean;
  provider: Provider | null;
  model: string | null;
  baseUrl: string | null;
  keyLast4: string | null;
}

/**
 * Bring-your-own-key for the AI assistant. The customer stores their own provider
 * key; the assistant then uses it (no platform cost, no monthly cap). The key is
 * write-only — we only ever show provider/model/last-4. Uses direct fetch (this is
 * a secret, deliberately not on the SDK surface).
 */
export function AiKeySettings() {
  const { baseUrl, token, entitlements } = useAuth();
  const url = `${baseUrl.replace(/\/$/, '')}/ai-key`;
  const authHeaders = { authorization: `Bearer ${token}`, 'content-type': 'application/json' };

  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [provider, setProvider] = useState<Provider>('anthropic');
  const [model, setModel] = useState('claude-sonnet-5');
  const [apiBase, setApiBase] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () =>
    fetch(url, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => s && setStatus(s))
      .catch(() => {});

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    if (token) load();
  }, [token]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          provider,
          key: key.trim(),
          model: model.trim(),
          ...(provider === 'openai' && apiBase.trim() ? { baseUrl: apiBase.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Failed to save (${res.status})`);
      }
      setKey('');
      await load();
      setMsg('Saved. The assistant now uses your key.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await fetch(url, { method: 'DELETE', headers: authHeaders });
      await load();
      setMsg('Removed. The assistant uses the built-in model again.');
    } finally {
      setBusy(false);
    }
  };

  // Gated feature — hidden unless the account has the BYOK entitlement enabled.
  if (!entitlements.byok) return null;

  return (
    <Card>
      <h2 className="font-semibold">Bring your own AI key</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Use your own provider key for <b>Ask AI</b>. When set, queries run on your account with your
        provider — billed by them, not us, with no monthly cap. Your key is stored encrypted and
        never shown again.
      </p>

      {status?.keySet && (
        <div className="mt-3 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm dark:border-emerald-900 dark:bg-emerald-950/30">
          <span>
            Using <b>{status.provider === 'anthropic' ? 'Anthropic' : 'OpenAI-compatible'}</b>
            {status.model ? ` · ${status.model}` : ''} · key ••••{status.keyLast4}
          </span>
          <Button variant="ghost" size="sm" onClick={remove} disabled={busy}>
            Remove
          </Button>
        </div>
      )}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field label="Provider">
          <select
            className={inputClass}
            value={provider}
            onChange={(e) => setProvider(e.target.value as Provider)}
          >
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI-compatible</option>
          </select>
        </Field>
        <Field label="Model">
          <input
            className={inputClass}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={provider === 'anthropic' ? 'claude-sonnet-4-6' : 'gpt-4o-mini'}
          />
        </Field>
        {provider === 'openai' && (
          <Field label="Base URL (optional — Azure/OpenRouter/etc.)">
            <input
              className={inputClass}
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              placeholder="https://api.openai.com/v1"
            />
          </Field>
        )}
        <Field label={status?.keySet ? 'Replace API key' : 'API key'}>
          <input
            className={inputClass}
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={provider === 'anthropic' ? 'sk-ant-…' : 'sk-…'}
            autoComplete="off"
          />
        </Field>
      </div>

      {msg && <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{msg}</p>}
      <div className="mt-3">
        <Button onClick={save} loading={busy} disabled={!key.trim() || !model.trim()}>
          {status?.keySet ? 'Update key' : 'Save key'}
        </Button>
      </div>
    </Card>
  );
}
